import React, { useEffect, useRef } from 'react';
import { GraphQLClient } from 'graphql-request';
import { useQuery } from '@tanstack/react-query';
import { getEnvGQLUrl } from '../../../helpers';
import { GetSearchSuggestionsDocument } from '../../../../codegenGenerated/optigraphql/generated';

export interface SearchSuggestionsProps {
  maxResults: number;
  searchTerm: string;
  onSuggestionClick: (suggestion: string) => void;
  minCharsToSearch: number;
  focusedIndex: number;
  vuid?: string;
  personalizedSuggestionsEndpoint?: string;
  isFocused?: boolean;
}

interface AutocompleteResponse {
  Content: {
    autocomplete: {
      PageSearchHeadline: string[];
    };
  };
}

interface PersonalizedSuggestionsResponse {
  data: {
    events: {
      edges: Array<{
        node: {
          ts: string;
          search_term: string;
        };
      }>;
    };
  };
}

const useGetSearchSuggestions = (endpoint: string, maxResults: number, searchTerm: string) => {
  const graphQLClient = new GraphQLClient(endpoint);

  const { isError, error, isFetching, data } = useQuery<AutocompleteResponse>({
    queryKey: ['GetSearchSuggestions', searchTerm],
    queryFn: async () =>
      graphQLClient.request(GetSearchSuggestionsDocument, {
        numberOfResults: maxResults,
        searchTerm: searchTerm
      }),
    refetchOnWindowFocus: false,
    networkMode: 'always',
    enabled: searchTerm.length >= 3
  });

  return {
    status: isError ? 'error' : isFetching ? 'loading' : 'success',
    error: error as Error,
    results:
      data?.Content?.autocomplete?.PageSearchHeadline.map(suggestion =>
        suggestion.replace(/\n/g, ' ').replace(/^[.]+/, '').trim()
      ) || []
  };
};

const useGetPersonalizedSuggestions = (vuid?: string, endpoint?: string) => {
  const { isError, error, isFetching, data } = useQuery<PersonalizedSuggestionsResponse>({
    queryKey: ['GetPersonalizedSuggestions', vuid],
    queryFn: async () => {
      if (!vuid || !endpoint) {
        return { data: { events: { edges: [] } } };
      }
      const response = await fetch(`${endpoint}?vuid=${vuid}`);
      if (!response.ok) {
        throw new Error('Failed to fetch personalized suggestions');
      }
      const data = await response.json();
      return data;
    },
    refetchOnWindowFocus: false,
    networkMode: 'always',
    enabled: !!vuid && !!endpoint
  });

  // Extract unique search terms from the response, sorted by most recent timestamp
  const suggestions = data?.data?.events?.edges
    ? [
        ...new Set(
          data.data.events.edges
            .sort((a, b) => parseInt(b.node.ts) - parseInt(a.node.ts)) // Sort by timestamp descending
            .map(edge => edge.node.search_term)
        )
      ].slice(0, 3) // Take only the first 3 unique suggestions
    : [];

  return {
    status: isError ? 'error' : isFetching ? 'loading' : 'success',
    error: error as Error,
    suggestions
  };
};

/**
 * Highlights the search term within a suggestion
 * @param suggestion The full suggestion text
 * @param searchTerm The search term to highlight
 * @returns JSX with highlighted search term
 */
const highlightMatch = (suggestion: string, searchTerm: string) => {
  if (!searchTerm || searchTerm.trim() === '') {
    return <>{suggestion}</>;
  }

  const lowerSearchTerm = searchTerm.toLowerCase();
  const lowerSuggestion = suggestion.toLowerCase();
  const index = lowerSuggestion.indexOf(lowerSearchTerm);

  if (index === -1) {
    return <>{suggestion}</>;
  }

  return (
    <>
      {suggestion.substring(0, index)}
      <strong>{suggestion.substring(index, index + lowerSearchTerm.length)}</strong>
      {suggestion.substring(index + lowerSearchTerm.length)}
    </>
  );
};

export const SearchSuggestions: React.FC<SearchSuggestionsProps> = ({
  maxResults,
  searchTerm,
  minCharsToSearch,
  onSuggestionClick,
  focusedIndex,
  vuid,
  personalizedSuggestionsEndpoint,
  isFocused = false
}) => {
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // Only fetch general suggestions when search term meets minimum length
  const { status: generalStatus, results } = useGetSearchSuggestions(
    getEnvGQLUrl(),
    Number(maxResults ?? 3),
    searchTerm ?? ''
  );

  // Always fetch personalized suggestions when the component is rendered
  const { suggestions: personalizedSuggestions } =
    useGetPersonalizedSuggestions(vuid, personalizedSuggestionsEndpoint);

  // Limit suggestions to maximum of 3
  const limitedSuggestions = personalizedSuggestions.slice(0, 3);

  // Handle scrolling when focusedIndex changes
  useEffect(() => {
    if (suggestionsRef.current && focusedIndex >= 0) {
      const focusedElement = suggestionsRef.current.querySelector(
        `[data-suggestion-index="${focusedIndex}"]`
      );
      if (focusedElement) {
        focusedElement.scrollIntoView({
          block: 'nearest',
          behavior: 'smooth'
        });
      }
    }
  }, [focusedIndex]);

  // Determine whether to show general suggestions based on search term length
  const shouldShowGeneralSuggestions = (searchTerm ?? '').length >= minCharsToSearch;

  // Check if we have any suggestions to show
  const hasPersonalizedSuggestions = limitedSuggestions.length > 0;
  const hasGeneralSuggestions = shouldShowGeneralSuggestions && generalStatus !== 'error' && results.length > 0;

  // Determine which type of suggestions to show - now allow personalized suggestions
  // to show regardless of search term length, but prioritize general suggestions
  // when they are available
  const showGeneralSuggestions = hasGeneralSuggestions;
  // Show personalized suggestions if they exist and general suggestions don't
  const showPersonalizedSuggestions = hasPersonalizedSuggestions && !showGeneralSuggestions;

  // Don't render anything if:
  // 1. The search bar is not focused and the search term is less than min chars, OR
  // 2. No suggestions of any kind to show
  if (
    (!isFocused && (searchTerm ?? '').length < minCharsToSearch && !hasPersonalizedSuggestions) ||
    (!showPersonalizedSuggestions && !showGeneralSuggestions)
  ) {
    return null;
  }

  // Handle suggestion click with proper event handling
  const handleSuggestionClick = (suggestion: string) => {
    // Ensure suggestion is a string and not undefined/null
    if (suggestion) {
      // Call the parent component's click handler
      onSuggestionClick(suggestion);
    }
  };

  return (
    <div
      ref={suggestionsRef}
      className="search-bar__panel__top__input-wrapper__suggestions"
      role="listbox"
      id="search-suggestions"
    >
      {/* Personalized Suggestions Section */}
      {showPersonalizedSuggestions && (
        <>
          {limitedSuggestions.map((suggestion, index) => (
            <div
              key={`suggestion-${index}`}
              onClick={() => handleSuggestionClick(suggestion)}
              className={`search-bar__panel__top__input-wrapper__suggestions-item ${
                index === focusedIndex ? 'focused' : ''
              }`}
              role="option"
              aria-selected={index === focusedIndex}
              id={`suggestion-${index}`}
              data-suggestion-index={index}
            >
              {highlightMatch(suggestion, searchTerm)}
            </div>
          ))}
        </>
      )}

      {/* General Suggestions Section */}
      {showGeneralSuggestions && (
        <>
          {results.map((suggestion, index) => (
            <div
              key={`general-${index}`}
              onClick={() => handleSuggestionClick(suggestion)}
              className={`search-bar__panel__top__input-wrapper__suggestions-item ${
                index === focusedIndex ? 'focused' : ''
              }`}
              role="option"
              aria-selected={index === focusedIndex}
              id={`suggestion-${index}`}
              data-suggestion-index={index}
            >
              {highlightMatch(suggestion, searchTerm)}
            </div>
          ))}
        </>
      )}
    </div>
  );
};
